import { HOME_SEO_BODY as B } from "@/data/home_seo_body";

/* HomeSeoBody — the homepage's indexable body copy, rendered EAGERLY.
 *
 * v60.99.120 — must never be wrapped in <DeferUntilVisible>. Every other
 * section below the hero mounts on IntersectionObserver, which Googlebot never
 * triggers because it does not scroll; that is why the homepage was serving
 * 346 words to crawlers and had fallen out of the index entirely. This block
 * is plain text, a table and a details list — no images, no charts, no
 * third-party calls — so rendering it up front costs almost nothing on the
 * LCP path while guaranteeing the page has real content for both crawlers and
 * readers. Copy lives in src/data/home_seo_body.js, shared with the static
 * prerender pipeline.
 */

const H2 = ({ children }) => (
  <h2 className="font-display text-base md:text-lg font-black tracking-tight mb-3 text-zinc-900">{children}</h2>
);

export default function HomeSeoBody() {
  return (
    <section
      className="bg-white border-t border-zinc-200"
      aria-label="About FeedPulse"
      data-testid="home-seo-body"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
        <p className="text-base text-zinc-700 leading-relaxed mb-12 sm:mb-16">{B.intro}</p>

        <div className="space-y-12 sm:space-y-16">
          {B.sections.map((s) => (
            <div key={s.h2} data-testid={`home-seo-section-${s.h2.slice(0, 18).toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <H2>{s.h2}</H2>
              {s.paras.map((p) => (
                <p key={p.slice(0, 24)} className="text-sm text-zinc-700 leading-relaxed mb-3">
                  {p}
                </p>
              ))}

              {s.bullets ? (
                <ul className="mt-4 space-y-1.5">
                  {s.bullets.map((b) => (
                    <li key={b} className="text-sm text-zinc-700 flex gap-2.5">
                      <span className="text-klein font-black leading-6" aria-hidden="true">
                        /
                      </span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              ) : null}

              {s.chips ? (
                <div
                  className="mt-5 flex flex-wrap gap-2"
                  data-testid="home-seo-section-chips"
                >
                  {s.chips.map((k) => (
                    <span key={k} className="px-2 py-1 bg-zinc-100 text-zinc-700 text-xs font-mono">{k}</span>
                  ))}
                </div>
              ) : null}

              {s.table ? (
                <div className="mt-6 overflow-x-auto">
                  <table className="w-full border-collapse text-sm" data-testid="home-comparison-table">
                    <thead>
                      <tr>
                        {s.table.head.map((h) => (
                          <th
                            key={h || "feature"}
                            scope="col"
                            className="border border-zinc-200 bg-zinc-50 px-3 py-2 text-left text-[11px] font-mono uppercase tracking-widest text-zinc-500 whitespace-nowrap"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {s.table.rows.map((row) => (
                        <tr key={row[0]}>
                          {row.map((cell, i) => (
                            <td
                              key={i}
                              className={`border border-zinc-200 px-3 py-2 ${
                                i === 0 ? "font-semibold text-zinc-900" : "text-zinc-700"
                              } ${i === 1 ? "bg-klein/5 font-semibold" : ""}`}
                            >
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}

              {s.note ? <p className="mt-3 text-xs text-zinc-500 leading-relaxed">{s.note}</p> : null}
            </div>
          ))}
        </div>

        <div className="mt-14 sm:mt-20 pt-10 border-t border-zinc-200">
          <H2>Frequently asked questions</H2>
          <dl className="mt-5 space-y-6">
            {B.faq.map((f) => (
              <div key={f.q}>
                <dt className="text-sm font-bold text-zinc-900 mb-1.5">{f.q}</dt>
                <dd className="text-sm text-zinc-700 leading-relaxed">{f.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </section>
  );
}
